"use client";

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type { ItemSelectorItem } from "@/components/ItemSelectorDialog";
import {
  buildLabelFromFieldsForExport,
  fetchAllRelatedItems,
} from "@/app/component/relationImportLookup";
import { relationLabelCache } from "@/app/utils/relationLabelCacheStore";

export type ItemSelectorDetailFieldMeta = {
  name: string;
  label?: string;
  type?: string;
  value?: string;
  fields?: unknown;
  sourceField?: string;
};

export type ItemSelectorDetailRow = {
  key: string;
  header: string;
  display: string;
  multiline: boolean;
};

function isRelationFieldType(type?: string): boolean {
  return (
    type === "relation" ||
    type === "multiplerelation" ||
    type === "systemrelation"
  );
}

function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== "object" || !path) return undefined;
  if (!path.includes(".")) return (obj as Record<string, unknown>)[path];
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object") return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function formatPlainDetailValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function relationUidFromValue(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" || typeof value === "number") {
    const s = String(value).trim();
    return s || null;
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const uid = o.uid ?? o.id;
    return uid != null ? String(uid).trim() : null;
  }
  return null;
}

function labelFromEmbeddedRelationObject(
  value: unknown,
  fieldMeta: ItemSelectorDetailFieldMeta
): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const built = buildLabelFromFieldsForExport(
    value as Record<string, unknown>,
    fieldMeta.fields
  );
  if (built) return built;
  const o = value as Record<string, unknown>;
  const parts = [o.name, o.label, o.title].filter(
    (v) => v != null && String(v).trim() !== ""
  );
  return parts.length ? parts.map(String).join(" ") : null;
}

function resolveRelationFieldDisplay(
  value: unknown,
  lookup: Map<string, string>,
  fieldMeta: ItemSelectorDetailFieldMeta
): string {
  if (value == null || value === "") return "—";

  if (fieldMeta.type === "multiplerelation") {
    const items = Array.isArray(value) ? value : [value];
    const labels = items
      .map((entry) => resolveRelationFieldDisplay(entry, lookup, { ...fieldMeta, type: "relation" }))
      .filter((t) => t !== "—");
    return labels.length ? labels.join(", ") : "—";
  }

  const embedded = labelFromEmbeddedRelationObject(value, fieldMeta);
  if (embedded) {
    const uid = relationUidFromValue(value);
    if (uid && lookup.has(uid)) {
      const resolved = lookup.get(uid)!;
      return embedded !== uid ? `${resolved}` : resolved;
    }
    return embedded;
  }

  const uid = relationUidFromValue(value);
  if (uid) {
    const resolved = lookup.get(uid) ?? lookup.get(uid.toLowerCase());
    if (resolved) return resolved;
    return uid;
  }

  return formatPlainDetailValue(value);
}

async function fetchRelationLabelLookup(
  slug: string,
  fieldType: string,
  displayFields: unknown
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const cache = relationLabelCache[slug];
  if (cache) {
    cache.forEach((label, uid) => map.set(uid, label));
  }

  const items = await fetchAllRelatedItems(slug, fieldType);
  for (const item of items) {
    const uidRaw = item.uid ?? item.id;
    if (uidRaw == null) continue;
    const uid = String(uidRaw);
    const built = buildLabelFromFieldsForExport(item, displayFields);
    const label =
      built ||
      (item.name != null ? String(item.name) : "") ||
      (item.title != null ? String(item.title) : "") ||
      (item.label != null ? String(item.label) : "") ||
      uid;
    map.set(uid, label);
    map.set(uid.toLowerCase(), label);
    if (!cache) {
      relationLabelCache[slug] = new Map();
    }
    relationLabelCache[slug].set(uid, label);
  }
  return map;
}

function formatFieldDisplay(
  value: unknown,
  fieldMeta: ItemSelectorDetailFieldMeta | undefined,
  lookups: Record<string, Map<string, string>>
): string {
  if (!fieldMeta || !isRelationFieldType(fieldMeta.type) || !fieldMeta.value) {
    return formatPlainDetailValue(value);
  }
  const slug = String(fieldMeta.value);
  const lookup = lookups[slug] ?? new Map();
  return resolveRelationFieldDisplay(value, lookup, fieldMeta);
}

function buildDetailRows<T>(
  item: ItemSelectorItem<T>,
  collectionFields: ItemSelectorDetailFieldMeta[] | undefined,
  lookups: Record<string, Map<string, string>>
): ItemSelectorDetailRow[] {
  const data =
    item.data != null && typeof item.data === "object" && !Array.isArray(item.data)
      ? (item.data as Record<string, unknown>)
      : null;

  const fieldByName = new Map(
    (collectionFields ?? []).map((f) => [String(f.name).toLowerCase(), f])
  );

  const rows: ItemSelectorDetailRow[] = [];
  const usedKeys = new Set<string>();

  if (collectionFields?.length && data) {
    for (const field of collectionFields) {
      const raw = getNestedValue(data, field.name);
      if (raw === undefined) continue;
      usedKeys.add(field.name);
      const display = formatFieldDisplay(raw, field, lookups);
      rows.push({
        key: field.name,
        header: field.label || field.name,
        display,
        multiline: display.includes("\n"),
      });
    }
  } else if (data) {
    for (const [key, raw] of Object.entries(data).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    )) {
      const fieldMeta = fieldByName.get(key.toLowerCase());
      const display = formatFieldDisplay(raw, fieldMeta, lookups);
      rows.push({
        key,
        header: fieldMeta?.label || fieldMeta?.name || key,
        display,
        multiline: display.includes("\n"),
      });
    }
    return rows;
  }

  if (!data) {
    const fallback: [string, unknown][] = [
      ["label", item.label],
      ["value", item.value],
    ];
    if (item.cells) {
      for (const [key, val] of Object.entries(item.cells)) {
        if (!fallback.some(([k]) => k === key)) fallback.push([key, val]);
      }
    }
    for (const [key, raw] of fallback) {
      const fieldMeta = fieldByName.get(key.toLowerCase());
      const display = formatFieldDisplay(raw, fieldMeta, lookups);
      rows.push({
        key,
        header: fieldMeta?.label || key,
        display,
        multiline: display.includes("\n"),
      });
    }
  }

  if (data) {
    for (const [key, raw] of Object.entries(data).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    )) {
      if (usedKeys.has(key)) continue;
      const fieldMeta = fieldByName.get(key.toLowerCase());
      const display = formatFieldDisplay(raw, fieldMeta, lookups);
      rows.push({
        key,
        header: fieldMeta?.label || key,
        display,
        multiline: display.includes("\n"),
      });
    }
  }

  return rows;
}

export function useResolvedItemDetailRows<T>(
  item: ItemSelectorItem<T> | null,
  collectionFields?: ItemSelectorDetailFieldMeta[]
) {
  const [lookups, setLookups] = useState<Record<string, Map<string, string>>>({});
  const [resolving, setResolving] = useState(false);

  const relationFields = useMemo(() => {
    if (!collectionFields?.length) return [];
    const seen = new Set<string>();
    return collectionFields.filter((f) => {
      if (!isRelationFieldType(f.type) || !f.value) return false;
      const slug = String(f.value);
      if (seen.has(slug)) return false;
      seen.add(slug);
      return true;
    });
  }, [collectionFields]);

  useEffect(() => {
    if (!item || relationFields.length === 0) {
      setLookups({});
      setResolving(false);
      return;
    }

    let cancelled = false;
    setResolving(true);

    (async () => {
      const next: Record<string, Map<string, string>> = {};
      await Promise.all(
        relationFields.map(async (field) => {
          const slug = String(field.value);
          const map = await fetchRelationLabelLookup(
            slug,
            field.type || "relation",
            field.fields
          );
          next[slug] = map;
        })
      );
      if (!cancelled) {
        setLookups(next);
        setResolving(false);
      }
    })().catch(() => {
      if (!cancelled) setResolving(false);
    });

    return () => {
      cancelled = true;
    };
  }, [item, relationFields]);

  const rows = useMemo(
    () => (item ? buildDetailRows(item, collectionFields, lookups) : []),
    [item, collectionFields, lookups]
  );

  return { rows, resolving };
}

export function ItemSelectorResolvedDetailsBody<T>({
  item,
  collectionFields,
}: {
  item: ItemSelectorItem<T>;
  collectionFields?: ItemSelectorDetailFieldMeta[];
}) {
  const { rows, resolving } = useResolvedItemDetailRows(item, collectionFields);

  if (resolving) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Resolving related values…
      </div>
    );
  }

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No details available.</p>;
  }

  return (
    <dl className="max-h-[min(60vh,420px)] space-y-3 overflow-y-auto pr-1">
      {rows.map((row) => (
        <div
          key={row.key}
          className="grid gap-1 border-b border-border/50 pb-3 last:border-0 last:pb-0 sm:grid-cols-[minmax(7rem,32%)_1fr] sm:gap-x-4"
        >
          <dt className="break-all text-sm font-medium text-muted-foreground">
            {row.header}
          </dt>
          <dd
            className={
              row.multiline
                ? "break-all whitespace-pre-wrap font-mono text-xs leading-relaxed"
                : "break-all text-sm"
            }
          >
            {row.display}
          </dd>
        </div>
      ))}
    </dl>
  );
}
